import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, Check, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TeamModal({ client, staff, headers, onClose, onSaved }) {
  const [selected, setSelected] = useState(client.assigned_staff_ids || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/clients/${client.id}/team`, { staff_ids: selected }, { headers });
      onSaved();
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="team-modal">
      <div className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-md w-full p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="close-team-modal"><X size={18} /></button>
        <h3 className="font-playfair text-xl text-white mb-1">Assign Team</h3>
        <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-widest mb-5">{client.name}</p>
        {staff.length === 0 ? (
          <p className="text-white/30 font-manrope text-sm py-4">No staff members yet — add staff in the Staff tab first.</p>
        ) : (
          <div className="space-y-2 mb-6 max-h-72 overflow-y-auto">
            {staff.map(s => (
              <button key={s.id} onClick={() => toggle(s.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border transition-colors ${selected.includes(s.id) ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/25'}`}
                data-testid={`team-staff-${s.id}`}>
                <div className="text-left">
                  <p className="text-white font-manrope text-sm">{s.name}</p>
                  <p className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">{s.title || (s.role || '').replace('_', ' ')}</p>
                </div>
                {selected.includes(s.id) && <Check size={15} className="text-[#D4AF37]" />}
              </button>
            ))}
          </div>
        )}
        <button onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3 rounded-sm hover:bg-[#C5A059] transition-colors disabled:opacity-50" data-testid="save-team-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : `Save Team (${selected.length})`}
        </button>
      </div>
    </div>
  );
}

const regStatusColors = {
  pending: 'bg-yellow-500/10 text-yellow-500',
  approved: 'bg-green-500/10 text-green-500',
  rejected: 'bg-red-500/10 text-red-400',
};

export function WarrantyRegistrationsBlock({ headers }) {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/warranty-registrations`, { headers });
      setRegs(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/warranty-registrations/${id}`, { status }, { headers });
      load();
    } catch (err) { console.error(err); }
  };

  if (loading) return null;

  return (
    <div className="mb-8" data-testid="admin-warranty-registrations">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={15} className="text-[#D4AF37]" />
        <h3 className="font-manrope text-white/60 text-xs uppercase tracking-widest">Warranty Registrations</h3>
        {regs.filter(r => r.status === 'pending').length > 0 && (
          <span className="bg-yellow-500/15 text-yellow-500 px-2 py-0.5 rounded-full font-manrope text-[10px] font-bold">{regs.filter(r => r.status === 'pending').length} pending</span>
        )}
      </div>
      {regs.length === 0 ? (
        <div className="bg-[#111113] border border-white/5 rounded-sm p-5 text-white/30 font-manrope text-xs" data-testid="no-warranty-regs">No warranty registrations yet — clients register from the portal's Warranty & Maintenance section</div>
      ) : (
        <div className="space-y-2">
          {regs.map(r => (
            <div key={r.id} className="bg-[#111113] border border-white/5 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-warranty-${r.id}`}>
              <div className="min-w-0">
                <p className="text-white font-manrope text-sm font-semibold">{r.theater_name || 'Theater'} — {r.client_name}</p>
                <p className="text-white/30 font-manrope text-xs">{r.client_email} · Installed {r.install_date}{r.invoice_number ? ` · Invoice ${r.invoice_number}` : ''}{r.address ? ` · ${r.address}` : ''}</p>
                {r.notes && <p className="text-white/40 font-manrope text-xs mt-1">{r.notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${regStatusColors[r.status] || 'bg-white/5 text-white/40'}`}>{r.status}</span>
                {r.status === 'pending' && (
                  <>
                    <button onClick={() => review(r.id, 'approved')} className="text-green-500/70 hover:text-green-500 border border-green-500/20 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`approve-warranty-${r.id}`}>Approve</button>
                    <button onClick={() => review(r.id, 'rejected')} className="text-red-400/60 hover:text-red-400 border border-red-400/20 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`reject-warranty-${r.id}`}>Reject</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
